import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTheme, applyTheme } from '../../contexts/ThemeContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import FeedbackModal from '../FeedbackModal';
import './AppShell.css';

// The one frame every signed-in page lives in: brand, navigation, plan, theme,
// feedback and sign-out. Pages render into <Outlet /> and own nothing of this.

const NAV = [
  { to: '/portfolio', label: 'Portfolio' },
  { to: '/chat', label: 'Ask the analyst' },
  { to: '/settings', label: 'Holdings & profile' },
];

function initialsOf(user) {
  const name = user?.displayName || user?.email || '';
  const parts = name.split(/[\s@]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase() || '·';
}

const Icon = {
  menu: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
      <path d="M3 5h14M3 10h14M3 15h14" />
    </svg>
  ),
  close: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
      <path d="M5 5l10 10M15 5L5 15" />
    </svg>
  ),
  sun: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
      <circle cx="8" cy="8" r="3" />
      <path d="M8 1v1.5M8 13.5V15M1 8h1.5M13.5 8H15M3 3l1 1M12 12l1 1M3 13l1-1M12 4l1-1" />
    </svg>
  ),
  moon: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M13.5 9.5A6 6 0 0 1 6.5 2.5a6 6 0 1 0 7 7z" />
    </svg>
  ),
  feedback: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2z" />
    </svg>
  ),
  logout: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6.5 2.5h-3v11h3M10 5l3 3-3 3M13 8H6" />
    </svg>
  ),
};

export default function AppShell({ user, onLogout }) {
  const { theme, toggleTheme } = useTheme();
  const { currency } = useCurrency();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const isPremium = Boolean(user?.isPremium);
  const name = user?.displayName || user?.email || 'Account';

  // The public landing page strips the theme attribute; restore it inside the app.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="dd-shell">
      <aside className={`dd-rail ${menuOpen ? 'open' : ''}`}>
        <div className="dd-rail-top">
          <NavLink to="/portfolio" className="dd-brand dd-focus">
            <img src="/icons/icon-128.webp" alt="" width="28" height="28" />
            <span className="dd-serif dd-wordmark">DeepDime</span>
          </NavLink>
          <button
            type="button"
            className="dd-menu-btn dd-focus"
            aria-expanded={menuOpen}
            aria-controls="dd-rail-body"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? Icon.close : Icon.menu}
          </button>
        </div>

        <div className="dd-rail-body" id="dd-rail-body">
          <nav className="dd-nav" aria-label="Main">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `dd-nav-link dd-focus ${isActive ? 'on' : ''}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="dd-rail-foot">
            <div className="dd-plan dd-mono">
              {isPremium ? 'Pro plan' : 'Free plan'} · {currency === 'INR' ? '₹ INR' : '$ USD'}
            </div>
            {!isPremium && (
              <NavLink to="/premium" className="dd-rail-link dd-focus">Upgrade to Pro</NavLink>
            )}
            <button type="button" className="dd-rail-action dd-focus" onClick={toggleTheme}>
              {theme === 'dark' ? Icon.sun : Icon.moon}
              <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
            </button>
            <button type="button" className="dd-rail-action dd-focus" onClick={() => setFeedbackOpen(true)}>
              {Icon.feedback}
              <span>Send feedback</span>
            </button>
            <div className="dd-user">
              <span className="dd-avatar dd-mono" aria-hidden="true">{initialsOf(user)}</span>
              <span className="dd-user-name" title={name}>{name}</span>
              <button type="button" className="dd-icon-btn dd-focus" onClick={onLogout} aria-label="Sign out" title="Sign out">
                {Icon.logout}
              </button>
            </div>
          </div>
        </div>
      </aside>

      <main className="dd-main">
        <Outlet />
      </main>

      <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} user={user} />
    </div>
  );
}
